import { memo, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';

import Collapse from '@src/shared/ui/Collapse';
import { ArrowDownIcon, ArrowUpIcon } from '@src/shared/ui/icons';
import SidebarItem from './SidebarItem';
import SidebarSubItem from './SidebarSubItem';
import SidebarGroup from './SidebarGroup';

interface SidebarChildItem {
  key: string;
  label: string;
  to: string;
}

interface SidebarCollapsibleItemProps {
  label?: ReactNode;
  icon?: ReactNode;
  children: SidebarChildItem[];
}

const ICON_SX = { fontSize: 18 } as const;

function SidebarCollapsibleItem({ label, icon, children }: SidebarCollapsibleItemProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isChildActive = children.some((child) => pathname.startsWith(child.to));
  const [isOpen, setIsOpen] = useState(isChildActive);

  useEffect(() => {
    if (isChildActive) {
      setIsOpen(true);
    }
  }, [isChildActive]);

  const endIcon = isOpen
    ? <ArrowUpIcon color="inherit" sx={ICON_SX} />
    : <ArrowDownIcon color="inherit" sx={ICON_SX} />;

  return (
    <>
      <SidebarItem
        label={label}
        icon={icon}
        endIcon={endIcon}
        active={isChildActive}
        component="div"
        onClick={() => setIsOpen((prev) => !prev)}
        role="button"
        aria-expanded={isOpen}
      />

      <Collapse component="li" in={isOpen}>
        <SidebarGroup>
          {children.map((child) => (
            <SidebarSubItem
              key={child.key}
              label={child.label}
              to={child.to}
            />
          ))}
        </SidebarGroup>
      </Collapse>
    </>
  );
}

SidebarCollapsibleItem.displayName = 'SidebarCollapsibleItem';
export default memo(SidebarCollapsibleItem);
